import { serverSideConfigs, t } from 'localization'
import Head from 'next/head'
import { FC, useState } from 'react'
import { Button } from 'shared/components/Button'
import { FCLink } from 'shared/components/Links'
import { PagesQA } from 'shared/dataAttributes'
import { colors, fonts, media, styles } from 'shared/lib'
import { getAllMenSizes, getAllWomenSizes } from 'shared/lib/sizes'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import styled from 'styled-components'

const TP = 'page.sizeGuide'

enum TableType {
  Men,
  Women,
}

interface SizesTableProps {
  type: TableType
}

const menSizes = getAllMenSizes()
const womenSizes = getAllWomenSizes()

const SizesTable: FC<SizesTableProps> = ({ type }) => {
  const allSizes = { [TableType.Men]: menSizes, [TableType.Women]: womenSizes }[type]

  return (
    <Table>
      <thead>
        <tr>
          <th>{t(`${TP}.us`, 'US')}</th>
          <th>{t(`${TP}.uk`, 'UK')}</th>
          <th>{t(`${TP}.eu`, 'EU')}</th>
          <th>{t(`${TP}.cm`, 'CM')}</th>
        </tr>
      </thead>
      <tbody>
        {allSizes.map((sizes, i) => {
          const [US, UK, EU, CM] = sizes
          return (
            <tr key={i}>
              <td>{US}</td>
              <td>{UK}</td>
              <td>{EU}</td>
              <td>{CM}</td>
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}
const MensTable = () => <SizesTable type={TableType.Men} />
const WomensTable = () => <SizesTable type={TableType.Women} />

const SizeGuidePage = () => {
  const [tableType, setTableType] = useState(TableType.Men)
  const handleClick = (type) => () => setTableType(type)
  const SelectedTable = { [TableType.Men]: MensTable, [TableType.Women]: WomensTable }[tableType]
  return (
    <>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Size Guide | Flight Club')}</title>
      </Head>
      <Wrapper>
        <Article>
          <Header data-qa={PagesQA.SizeGuidePageTitle}>{t(`${TP}.sizeGuide`, 'Size Guide')}</Header>
          <Paragraph data-qa={PagesQA.SizeGuideDescription}>
            {t(
              `${TP}.needHelpWith`,
              'Need help with sizing? We are here to help you 7 days a week: 12pm - 7pm EST.',
            )}
            <br />
            <FCLink qaAttr={PagesQA.SizeGuideSupportEmail} href="mailto:support@flightclub.com">
              support@flightclub.com
            </FCLink>
          </Paragraph>
          <Mobile>
            <TableWrapper>
              <ToggleWrapper>
                <ToggleDisplayButton
                  $fill
                  buttonType="secondary"
                  isSelected={tableType === TableType.Men}
                  onClick={handleClick(TableType.Men)}
                >
                  {t(`${TP}.men`, 'Men')}
                </ToggleDisplayButton>
                <ToggleDisplayButton
                  $fill
                  buttonType="secondary"
                  isSelected={tableType === TableType.Women}
                  onClick={handleClick(TableType.Women)}
                >
                  {t(`${TP}.women`, 'Women')}
                </ToggleDisplayButton>
              </ToggleWrapper>
              <SelectedTable />
            </TableWrapper>
          </Mobile>
          <Desktop>
            <TableWrapper>
              <TableHeader>{t(`${TP}.men`, 'Men')}</TableHeader>
              <MensTable />
            </TableWrapper>
            <TableWrapper>
              <TableHeader>{t(`${TP}.women`, 'Women')}</TableHeader>
              <WomensTable />
            </TableWrapper>
          </Desktop>
        </Article>
      </Wrapper>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { res, locale } = context
  return {
    props: {
      ...(await getServerPageProps(context)),
      ...(await serverSideConfigs(res, locale)),
    },
  }
}

const Wrapper = styled.div`
  background-color: ${colors.FC2_WHITE};
  height: 100%;
  padding: ${styles.mobilePageLayoutPadding};
  ${media.large`
    display: flex;
    align-items: center;
    padding: 80px 150px 100px;
  `}
`

const Article = styled.article`
  width: 100%;
`

const Header = styled.h1`
  ${fonts.HEADER_1}
  margin: 20px 0;
`

const Paragraph = styled.p`
  ${fonts.BODY_TEXT}
  line-height: 16px;
`

const TableWrapper = styled.div`
  margin-top: 20px;
  padding: 20px 20px 15px;
  border: 1px solid ${colors.FC2_LIGHTEST_GREY};
`

const ToggleWrapper = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 1fr 1fr;
`

const ToggleDisplayButton = styled(Button)<{ isSelected: boolean }>`
  border-color: ${({ isSelected }) => (isSelected ? colors.FC2_BLACK : colors.FC2_LIGHTEST_GREY)};
`

const Table = styled.table`
  margin-top: 15px;
  width: 100%;

  th {
    padding: 7px 0;
    text-align: left;
    ${fonts.LINK_ACTION};
  }

  tbody td {
    padding: 7px 0;
    border-top: 1px solid ${colors.FC2_LIGHTEST_GREY};
  }
`

const TableHeader = styled.div`
  ${fonts.SUBTITLE_2}
`

const Mobile = styled.div`
  display: block;
  ${media.large`display: none;`}
`

const Desktop = styled.div`
  display: none;
  ${media.large`
    display: grid;
    grid-gap: 20px;
    grid-template-columns: 1fr 1fr;
  `}
`

export default SizeGuidePage
